import type { FeedbackHubConfig } from './types';
import { DiagnosticsCapture } from './diagnostics';
import { FeedbackUI } from './feedback-ui';
import { WIDGET_CSS } from './styles';

export type { FeedbackHubConfig, FeedbackCategory, FeedbackSeverity, CaptureType } from './types';

let instance: FeedbackUI | null = null;
let diagnostics: DiagnosticsCapture | null = null;

/**
 * Initialise the FeedbackHub widget.
 *
 * npm usage:
 *   import { initFeedback } from '@feedbackhub/widget';
 *   initFeedback({ apiKey: 'xxx', appName: 'My App' });
 *
 * CDN usage:
 *   <script src="feedback-widget.umd.js"
 *           data-api-key="xxx"
 *           data-app-name="My App"></script>
 */
export function initFeedback(config: FeedbackHubConfig): void {
  if (instance) {
    console.warn('[FeedbackHub] Already initialised. Call destroyFeedback() first.');
    return;
  }

  // Inject styles
  injectStyles();

  // Start silent diagnostics capture
  diagnostics = new DiagnosticsCapture(config.maxConsoleEntries ?? 200);
  diagnostics.start();

  // Mount the UI
  instance = new FeedbackUI(config, diagnostics);
  instance.mount();
}

/**
 * Tear down the FeedbackHub widget and stop all diagnostic capture.
 */
export function destroyFeedback(): void {
  diagnostics?.destroy();
  diagnostics = null;
  instance?.destroy();
  instance = null;

  // Remove injected styles
  document.getElementById('fh-widget-styles')?.remove();
}

// ─── Style injection ────────────────────────────────────────────────────

function injectStyles(): void {
  if (document.getElementById('fh-widget-styles')) return;
  const style = document.createElement('style');
  style.id = 'fh-widget-styles';
  style.textContent = WIDGET_CSS;
  document.head.appendChild(style);
}

// ─── Auto-init from <script> tag data attributes ────────────────────────

function autoInit(): void {
  // Find the script tag that loaded us
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[data-api-key]',
  );

  for (const script of scripts) {
    const apiKey = script.dataset.apiKey;
    if (!apiKey) continue;

    // Don't double-init
    if (script.dataset.fhInitialized === 'true') continue;
    script.dataset.fhInitialized = 'true';

    const config: FeedbackHubConfig = {
      apiKey,
      appName: script.dataset.appName,
      appVersion: script.dataset.appVersion,
      environment: script.dataset.environment,
      apiUrl: script.dataset.apiUrl,
      buttonPosition: (script.dataset.buttonPosition as FeedbackHubConfig['buttonPosition']) ?? undefined,
      themeColor: script.dataset.themeColor,
      userId: script.dataset.userId,
      maxRecordingSeconds: script.dataset.maxRecordingSeconds
        ? parseInt(script.dataset.maxRecordingSeconds, 10)
        : undefined,
      maxConsoleEntries: script.dataset.maxConsoleEntries
        ? parseInt(script.dataset.maxConsoleEntries, 10)
        : undefined,
      enableVideoRecording:
        script.dataset.enableVideoRecording !== undefined
          ? script.dataset.enableVideoRecording !== 'false'
          : undefined,
      enableScreenshot:
        script.dataset.enableScreenshot !== undefined
          ? script.dataset.enableScreenshot !== 'false'
          : undefined,
      enableMyFeedback:
        script.dataset.enableMyFeedback !== undefined
          ? script.dataset.enableMyFeedback !== 'false'
          : undefined,
    };

    initFeedback(config);
    break; // Only init once
  }
}

// Auto-init when DOM is ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}
